import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { MapPin, Phone, Clock, Shield, Thermometer, Lock, Calendar, CreditCard, Key, ExternalLink, AlertCircle, Eye, EyeOff, Loader2, Building, Heart, Stethoscope, Leaf } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { AuthService } from "@/services/authService";
import { toast } from "@/hooks/use-toast";
import { RegisterRequest } from "@/types/auth";
import { useResponsive } from "@/hooks/useResponsive";
import Navbar from "@/components/landing/Navbar";
import WhatsAppButton from "@/components/landing/WhatsAppButton";
import AboutSection from "@/components/landing/AboutSection";
import StatsSection from "@/components/landing/StatsSection";
import BackToTop from "@/components/landing/BackToTop";

const Index = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const { isMobile } = useResponsive();
  
  const [loginData, setLoginData] = useState({ email: "", password: "" });
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showRegisterPassword, setShowRegisterPassword] = useState(false);
  const [isCepLoading, setIsCepLoading] = useState(false);
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  
  const [registerData, setRegisterData] = useState<RegisterRequest>({
    email: "",
    password: "",
    nome_completo: "",
    cpf: "",
    telefone: "",
    data_nascimento: "",
    profissao: "",
    cep: "",
    logradouro: "",
    numero: "",
    complemento: "",
    bairro: "",
    cidade: "",
    estado: "",
    aceitou_termos: false
  });

  // Funções de formatação
  const formatCPF = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      return numbers.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    }
    return value;
  };

  const formatPhone = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 11) {
      if (numbers.length <= 10) {
        return numbers.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
      } else {
        return numbers.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
      }
    }
    return value;
  };

  const formatCEP = (value: string) => {
    const numbers = value.replace(/\D/g, '');
    if (numbers.length <= 8) {
      return numbers.replace(/(\d{5})(\d{3})/, '$1-$2');
    }
    return value;
  };

  const cleanFormatting = (value: string) => {
    return value.replace(/\D/g, '');
  };

  const handleInputChange = (field: keyof RegisterRequest, value: string) => {
    let formattedValue = value;
    
    switch (field) {
      case 'cpf':
        formattedValue = formatCPF(value);
        break;
      case 'telefone':
        formattedValue = formatPhone(value);
        break;
      case 'cep':
        formattedValue = formatCEP(value);
        break;
      default:
        formattedValue = value;
    }
    
    setRegisterData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  // Redirecionar usuário logado para seu dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const redirectPath = user.role === 'ADMIN' ? '/admin' : '/client';
      navigate(redirectPath);
    }
  }, [isAuthenticated, user, navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const response = await AuthService.login(loginData);
      
      if (response.success) {
        toast({
          title: "Login realizado com sucesso!",
          description: "Redirecionando para seu dashboard...",
        });
        
        // Recarregar a página para atualizar o contexto
        window.location.reload();
      } else {
        toast({
          title: "Erro no login",
          description: response.message || "Credenciais inválidas",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      toast({
        title: "Erro no login",
        description: error instanceof Error ? error.message : "Erro interno do servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast({
        title: "Termos obrigatórios",
        description: "Você deve aceitar os termos do contrato para se cadastrar",
        variant: "destructive",
      });
      return;
    }
    
    setIsLoading(true);
    
    try {
      const dataToSend = {
        ...registerData,
        cpf: cleanFormatting(registerData.cpf),
        telefone: cleanFormatting(registerData.telefone),
        cep: cleanFormatting(registerData.cep),
        aceitou_termos: acceptedTerms
      };
      
      const response = await AuthService.register(dataToSend);
      
      if (response.success) {
        toast({
          title: "Cadastro realizado com sucesso!",
          description: "Aguarde a aprovação do administrador para acessar o sistema.",
        });
        
        // Limpar formulário e fechar modal
        setRegisterData({
          email: "",
          password: "",
          nome_completo: "",
          cpf: "",
          telefone: "",
          data_nascimento: "",
          profissao: "",
          cep: "",
          logradouro: "",
          numero: "",
          complemento: "",
          bairro: "",
          cidade: "",
          estado: "",
          aceitou_termos: false
        });
        setAcceptedTerms(false);
        setIsRegisterModalOpen(false);
      } else {
        toast({
          title: "Erro no cadastro",
          description: response.message || "Erro ao criar conta",
          variant: "destructive",
        });
      }
    } catch (error: unknown) {
      toast({
        title: "Erro no cadastro",
        description: error instanceof Error ? error.message : "Erro interno do servidor",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCepBlur = async () => {
    const cleanCep = cleanFormatting(registerData.cep);
    
    
    
    if (cleanCep && cleanCep.length === 8) {
      setIsCepLoading(true);
      try {
        
        const response = await AuthService.searchCep(cleanCep);
        
        
        if (response && !response.erro) {
          setRegisterData(prev => ({
            ...prev,
            logradouro: response.logradouro || '',
            bairro: response.bairro || '',
            cidade: response.localidade || '',
            estado: response.uf || ''
          }));
          toast({
            title: "CEP encontrado!",
            description: "Endereço preenchido automaticamente.",
          });
          
        } else {
          
          toast({
            title: "CEP não encontrado",
            description: "Verifique o CEP informado e preencha o endereço manualmente.",
            variant: "destructive",
          });
        }
      } catch (error) {
        console.error('Erro ao buscar CEP:', error);
        toast({
          title: "Erro ao buscar CEP",
          description: "Preencha o endereço manualmente.",
          variant: "destructive",
        });
      } finally {
        setIsCepLoading(false);
      }
    } else {
      console.log('CEP não tem 8 dígitos ou está vazio');
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Se o usuário está logado, redirecionar
  if (isAuthenticated && user) {
    return null;
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-blue-50 relative overflow-hidden pt-16 md:pt-20">
      {/* CSS Variables for New Color Palette */}
      <style>{`
        :root {
          --primary-green: #2ECC71;
          --primary-blue: #3498DB;
          --accent-teal: #1ABC9C;
          --light-gray: #F8F9FA;
          --medium-gray: #6C757D;
          --dark-gray: #343A40;
          --white: #FFFFFF;
          --success: #28A745;
          --warning: #FFC107;
          --error: #DC3545;
          --info: #17A2B8;
        }
      `}</style>

      {/* Enhanced Background with Health/Wellness Theme */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-100/40 via-teal-100/40 to-blue-100/40"></div>
        <div className="absolute top-0 left-0 w-96 h-96 bg-emerald-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse"></div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-teal-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-2000"></div>
        <div className="absolute -bottom-8 left-20 w-96 h-96 bg-blue-200/20 rounded-full mix-blend-multiply filter blur-xl opacity-70 animate-pulse animation-delay-4000"></div>
        
        {/* Health-themed floating elements */}

        <div className="absolute bottom-32 right-1/4 opacity-10">
          <Stethoscope className="w-20 h-20 text-teal-500 animate-bounce" style={{animationDelay: '2s'}} />
        </div>
        <div className="absolute top-1/2 left-12 opacity-10">
          <Leaf className="w-16 h-16 text-emerald-400 animate-bounce" style={{animationDelay: '3s'}} />
        </div>
      </div>
      
      {/* Hero Section with Enhanced Branding */}
      <section id="hero" className="relative min-h-screen flex items-center justify-center py-8 md:py-0">
        <div className="w-full min-h-screen grid grid-cols-1 lg:grid-cols-2 relative z-10">
          
          {/* Left Side - Enhanced Brand & Content */}
          <div className="relative flex flex-col justify-center items-center lg:items-start px-4 sm:px-6 md:px-8 lg:px-16 py-10 md:py-0 text-center lg:text-left h-full order-2 lg:order-1">
            
            {/* Enhanced Logo/Brand Section */}
            <div>
              <div className="flex items-center justify-center lg:justify-start mb-4 lg:mb-6">
                <div className="w-14 h-14 sm:w-16 sm:h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mr-3 lg:mr-4 shadow-lg">
                  <Heart className="w-8 h-8 sm:w-10 sm:h-10 lg:w-12 lg:h-12 text-white" />
                </div>
                <div>
                  <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 bg-clip-text text-transparent leading-tight">
                    Vita Sana
                  </h1>
                </div>
              </div>
              
              <p className="text-lg sm:text-xl md:text-2xl lg:text-3xl text-gray-700 font-light mb-3 md:mb-4 leading-relaxed">
                Espaço de <span className="font-semibold text-emerald-600">Bem-Estar</span> e <span className="font-semibold text-teal-600">Saúde</span>
              </p>
              <p className="text-sm sm:text-base md:text-lg text-gray-600 mb-6 md:mb-8 max-w-lg mx-auto lg:mx-0">
                Coworking especializado para profissionais da saúde com ambiente acolhedor e tecnologia de ponta
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6">
                <button
                  type="button"
                  onClick={() => {
                    const el = document.getElementById("about");
                    if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" });
                  }}
                  className="px-6 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 text-sm sm:text-base"
                >
                  Conheça o Espaço
                </button>
                <a
                  href="https://wa.me/5555981524953"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-6 py-3 border-2 border-emerald-500 text-emerald-600 font-semibold rounded-xl hover:bg-emerald-50 transition-all duration-300 text-sm sm:text-base text-center"
                >
                  Agende uma Visita
                </a>
              </div>
              
              <div className="w-24 lg:w-32 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-blue-500 mx-auto lg:mx-0 rounded-full shadow-sm"></div>
            </div>


          </div>

          {/* Right Side - Enhanced Form */}
          <div className="relative flex items-center justify-center p-4 md:p-8 lg:p-16 order-1 lg:order-2" id="login-form">
            <div className="w-full max-w-md space-y-8">

              {/* Enhanced Login/Register Card */}
              <Card className="shadow-2xl bg-white/95 backdrop-blur-lg border-0 ring-1 ring-emerald-100/50 relative overflow-hidden rounded-[2.5rem]">
                {/* Enhanced Card background */}
                <div className="absolute inset-0 bg-gradient-to-br from-white/95 to-emerald-50/30"></div>
                
                <CardHeader className="text-center relative z-10 pb-6 lg:pb-8">
                  <div className="w-16 h-16 lg:w-20 lg:h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-3xl mx-auto mb-4 lg:mb-6 flex items-center justify-center shadow-lg">
                    <Key className="w-8 h-8 lg:w-10 lg:h-10 text-white" />
                  </div>
                  <CardTitle className="text-2xl lg:text-3xl font-bold text-gray-800 mb-2 lg:mb-3">Acesse sua conta</CardTitle>

                  <div className="w-16 lg:w-20 h-1.5 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-4 lg:mt-6 rounded-full"></div>
                </CardHeader>
                
                <CardContent className="relative z-10 px-6 lg:px-8 pb-6 lg:pb-8">
                  {/* Login Form */}
                  <form onSubmit={handleLogin} className="space-y-6">
                    <div className="space-y-3">
                      <Label htmlFor="email" className="text-gray-700 font-semibold">E-mail</Label>
                      <Input
                        id="email"
                        type="email"
                        placeholder="seu@email.com"
                        value={loginData.email}
                        onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                        required
                        disabled={isLoading}
                        className="h-9 border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 rounded-xl"
                      />
                    </div>
                    <div className="space-y-3">
                      <Label htmlFor="password" className="text-gray-700 font-semibold">Senha</Label>
                      <div className="relative">
                        <Input
                          id="password"
                          type={showPassword ? "text" : "password"}
                          placeholder="Digite sua senha"
                          value={loginData.password}
                          onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                          required
                          disabled={isLoading}
                          className="h-9 border-gray-200 focus:border-emerald-400 focus:ring-emerald-400 rounded-xl pr-12"
                        />
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="absolute right-0 top-0 h-9 px-4 hover:bg-transparent"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          {showPassword ? (
                            <EyeOff className="h-5 w-5 text-gray-500" />
                          ) : (
                            <Eye className="h-5 h-5 text-gray-500" />
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Enhanced Login Button */}
                    <Button 
                      type="submit" 
                      className="w-full h-11 bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 hover:from-emerald-700 hover:via-teal-700 hover:to-blue-700 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] mt-8"
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                          Entrando...
                        </>
                      ) : (
                        <>
                          <Heart className="w-5 h-5 mr-3" />
                          Entrar no VitaSana
                        </>
                      )}
                    </Button>
                  </form>
                  
                  {/* Divider */}
                  <div className="relative my-8">
                    <div className="absolute inset-0 flex items-center">
                      <span className="w-full border-t border-gray-200" />
                    </div>
                    <div className="relative flex justify-center text-sm">
                      <span className="px-4 bg-white text-gray-500">ou</span>
                    </div>
                  </div>
                  
                  {/* Register Button */}
                  <Dialog open={isRegisterModalOpen} onOpenChange={setIsRegisterModalOpen}>
                    <DialogTrigger asChild>
                      <Button 
                        variant="outline" 
                        className="w-full h-9 border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 font-semibold rounded-xl transition-all duration-300"
                      >
                        Criar nova conta
                      </Button>
                    </DialogTrigger>
                    
                    <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Criar Nova Conta</DialogTitle>
                        <DialogDescription>
                          Preencha os dados abaixo para se cadastrar no VitaSana
                        </DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleRegister} className="space-y-4">
                        {/* Primeira linha - Nome completo */}
                        <div className="space-y-2">
                          <Label htmlFor="nome_completo">Nome completo *</Label>
                          <Input
                            id="nome_completo"
                            placeholder="Seu nome completo"
                            value={registerData.nome_completo}
                            onChange={(e) => handleInputChange('nome_completo', e.target.value)}
                            required
                            disabled={isLoading}
                          />
                        </div>
                        
                        {/* Segunda linha - Email e Senha */}
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="register-email">E-mail *</Label>
                            <Input
                              id="register-email"
                              type="email"
                              placeholder="seu@email.com"
                              value={registerData.email}
                              onChange={(e) => handleInputChange('email', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="register-password">Senha *</Label>
                            <div className="relative">
                              <Input
                                id="register-password"
                                type={showRegisterPassword ? "text" : "password"}
                                placeholder="Digite sua senha"
                                value={registerData.password}
                                onChange={(e) => handleInputChange('password', e.target.value)}
                                required
                                disabled={isLoading}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                onClick={() => setShowRegisterPassword(!showRegisterPassword)}
                              >
                                {showRegisterPassword ? (
                                  <EyeOff className="h-4 w-4 text-gray-500" />
                                ) : (
                                  <Eye className="h-4 w-4 text-gray-500" />
                                )}
                              </Button>
                            </div>
                          </div>
                        </div>
                        
                        {/* Terceira linha - CPF, Telefone e Data Nascimento */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="cpf">CPF *</Label>
                            <Input
                              id="cpf"
                              placeholder="000.000.000-00"
                              value={registerData.cpf}
                              onChange={(e) => handleInputChange('cpf', e.target.value)}
                              maxLength={14}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="telefone">Telefone *</Label>
                            <Input
                              id="telefone"
                              placeholder="(00) 00000-0000"
                              value={registerData.telefone}
                              onChange={(e) => handleInputChange('telefone', e.target.value)}
                              maxLength={15}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="data_nascimento">Data de Nascimento *</Label>
                            <Input
                              id="data_nascimento"
                              type="date"
                              value={registerData.data_nascimento}
                              onChange={(e) => handleInputChange('data_nascimento', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                        </div>
                        
                        {/* Quarta linha - Profissão */}
                        <div className="space-y-2">
                          <Label htmlFor="profissao">Profissão</Label>
                          <Input
                            id="profissao"
                            placeholder="Ex: Psicólogo(a), Nutricionista, Terapeuta..."
                            value={registerData.profissao}
                            onChange={(e) => handleInputChange('profissao', e.target.value)}
                            disabled={isLoading}
                          />
                        </div>
                        
                        {/* Quinta linha - CEP e Logradouro */}
                        <div className="grid grid-cols-3 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="cep">CEP *</Label>
                            <div className="relative">
                              <Input
                                id="cep"
                                placeholder="00000-000"
                                value={registerData.cep}
                                onChange={(e) => handleInputChange('cep', e.target.value)}
                                onBlur={handleCepBlur}
                                maxLength={9}
                                required
                                disabled={isLoading}
                              />
                              {isCepLoading && (
                                <Loader2 className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 animate-spin text-gray-500" />
                              )}
                            </div>
                          </div>
                          
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="logradouro">Logradouro *</Label>
                            <Input
                              id="logradouro"
                              placeholder="Rua, Avenida, etc."
                              value={registerData.logradouro}
                              onChange={(e) => handleInputChange('logradouro', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                        </div>
                        
                        {/* Sexta linha - Número, Complemento e Bairro */}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="numero">Número *</Label>
                            <Input
                              id="numero"
                              placeholder="123"
                              value={registerData.numero}
                              onChange={(e) => handleInputChange('numero', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="complemento">Complemento</Label>
                            <Input
                              id="complemento"
                              placeholder="Apto, sala..."
                              value={registerData.complemento}
                              onChange={(e) => handleInputChange('complemento', e.target.value)}
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2 col-span-2">
                            <Label htmlFor="bairro">Bairro *</Label>
                            <Input
                              id="bairro"
                              placeholder="Nome do bairro"
                              value={registerData.bairro}
                              onChange={(e) => handleInputChange('bairro', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                        </div>
                        
                        {/* Sétima linha - Cidade e Estado */}
                        <div className="grid grid-cols-4 gap-4">
                          <div className="space-y-2 col-span-3">
                            <Label htmlFor="cidade">Cidade *</Label>
                            <Input
                              id="cidade"
                              placeholder="Nome da cidade"
                              value={registerData.cidade}
                              onChange={(e) => handleInputChange('cidade', e.target.value)}
                              required
                              disabled={isLoading}
                            />
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="estado">Estado *</Label>
                            <Input
                              id="estado"
                              placeholder="UF"
                              value={registerData.estado}
                              onChange={(e) => handleInputChange('estado', e.target.value)}
                              required
                              disabled={isLoading}
                              maxLength={2}
                            />
                          </div>
                        </div>
                        
                        {/* Termos do Contrato */}
                        <div className="flex items-start space-x-2 pt-4 border-t">
                          <Checkbox
                            id="terms"
                            checked={acceptedTerms}
                            onCheckedChange={(checked) => setAcceptedTerms(!!checked)}
                            disabled={isLoading}
                          />
                          <div className="grid gap-1.5 leading-none">
                            <Label
                              htmlFor="terms"
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                            >
                              Aceito os termos do contrato *
                            </Label>
                            <div className="flex items-center gap-2">
                              <a
                                href="/contrato.pdf"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                              >
                                <ExternalLink className="w-3 h-3" />
                                Ler contrato completo
                              </a>
                            </div>
                          </div>
                        </div>
                        
                        {/* Aviso sobre aprovação */}
                        <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                          <AlertCircle className="w-4 h-4 text-yellow-600 mt-0.5" />
                          <div className="text-sm text-yellow-800">
                            <p className="font-medium">Aguarde aprovação</p>
                            <p>Após o cadastro, você precisará aguardar a aprovação do administrador para acessar o sistema.</p>
                          </div>
                        </div>
                        
                        <Button 
                          type="submit" 
                          className="w-full h-11 bg-gradient-to-r from-emerald-600 via-teal-600 to-blue-600 hover:from-emerald-700 hover:via-teal-700 hover:to-blue-700 text-white font-bold text-lg rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02]"
                          disabled={isLoading || !acceptedTerms}
                        >
                          {isLoading ? (
                            <>
                              <Loader2 className="w-5 h-5 mr-3 animate-spin" />
                              Cadastrando...
                            </>
                          ) : (
                            <>
                              <Heart className="w-5 h-5 mr-3" />
                              Cadastrar no VitaSana
                            </>
                          )}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <AboutSection />
      <StatsSection />

      {/* Features Section */}
      <section id="features" className="py-12 lg:py-20 px-4 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 relative">
        <div className="absolute inset-0 bg-white/30"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent mb-4 lg:mb-6">
              Por que escolher o VitaSana?
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Um espaço pensado especialmente para profissionais da saúde, com toda a infraestrutura 
              e comodidades que você precisa para atender seus pacientes com excelência.
            </p>
            <div className="w-16 lg:w-24 h-1 bg-gradient-to-r from-emerald-500 to-teal-500 mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Thermometer className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Climatizado</h3>
                <p className="text-gray-600 leading-relaxed">
                  Ambiente com ar condicionado para o conforto de você e seus pacientes
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Shield className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Segurança Total</h3>
                <p className="text-gray-600 leading-relaxed">
                  Portaria com segurança e controle de acesso 24 horas
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Acesso Privativo</h3>
                <p className="text-gray-600 leading-relaxed">
                  Porta com fechadura eletrônica e senha individual para cada profissional
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Calendar className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Reserva Online</h3>
                <p className="text-gray-600 leading-relaxed">
                  Sistema online para escolher e reservar seus horários de forma prática
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <CreditCard className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Pagamento Online</h3>
                <p className="text-gray-600 leading-relaxed">
                  Praticidade total com pagamento online seguro e automatizado
                </p>
              </CardContent>
            </Card>

            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Lock className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800">Ambiente Silencioso</h3>
                <p className="text-gray-600 leading-relaxed">
                  Espaço projetado para oferecer tranquilidade e privacidade
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="spaces" className="py-12 lg:py-20 px-4 bg-gradient-to-br from-teal-50/50 to-blue-50/50 relative">
        <div className="absolute inset-0 bg-white/20"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-teal-600 to-blue-600 bg-clip-text text-transparent mb-4 lg:mb-6">
              Nossos Espaços
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Salas equipadas e preparadas para diferentes tipos de atendimento
            </p>
            <div className="w-16 lg:w-24 h-1 bg-gradient-to-r from-teal-500 to-blue-500 mx-auto mt-6"></div>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="p-10 bg-white/90 backdrop-blur-lg border-0 shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-[1.02] group relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <CardHeader className="relative z-10">
                                  <div className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <Building className="w-8 h-8 text-white" />
                  </div>
                <CardTitle className="text-3xl font-bold text-gray-800 mb-2">Sala de Atendimento</CardTitle>
                <CardDescription className="text-lg text-gray-600">
                  Ideal para psicólogos, nutricionistas e terapeutas
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 relative z-10">
                                  <div className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Ambiente acolhedor e privativo</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-gradient-to-r from-teal-500 to-blue-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Mobiliário confortável</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-gradient-to-r from-blue-500 to-emerald-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Isolamento acústico</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Ar condicionado</span>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="w-3 h-3 bg-gradient-to-r from-teal-500 to-emerald-500 rounded-full"></div>
                      <span className="text-gray-700 font-medium">Acesso com senha individual</span>
                    </div>
                  </div>
                <div className="pt-6">
                  <span className="inline-flex items-center gap-2 bg-gradient-to-r from-green-500 to-emerald-500 text-white px-4 py-2 rounded-full text-sm font-semibold shadow-lg">
                    <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                    Disponível para locação
                  </span>
                </div>
              </CardContent>
            </Card>

            <Card className="p-10 bg-white/70 backdrop-blur-lg border-0 shadow-xl transition-all duration-300 relative overflow-hidden opacity-75">
              <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-gray-400/10 to-gray-500/10 rounded-full -translate-y-16 translate-x-16"></div>
              <CardHeader className="relative z-10">
                <div className="w-16 h-16 bg-gradient-to-br from-gray-400 to-gray-500 rounded-2xl flex items-center justify-center mb-4">
                  <Building className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-3xl font-bold text-gray-600 mb-2">Sala de Escritório</CardTitle>
                <CardDescription className="text-lg text-gray-500">
                  Espaço para trabalho administrativo
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6 relative z-10">
                <div className="space-y-4">
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-500 font-medium">Ambiente corporativo</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-500 font-medium">Mesa e cadeiras executivas</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-500 font-medium">Ar condicionado</span>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
                    <span className="text-gray-500 font-medium">Internet de alta velocidade</span>
                  </div>
                </div>
                <div className="pt-6">
                  <span className="inline-flex items-center gap-2 bg-gray-200 text-gray-600 px-4 py-2 rounded-full text-sm font-semibold">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    Temporariamente indisponível
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-12 lg:py-20 px-4 bg-gradient-to-br from-blue-50/50 to-emerald-50/50 relative">
        <div className="absolute inset-0 bg-white/40"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="text-center mb-10 lg:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-blue-600 to-emerald-600 bg-clip-text text-transparent mb-4 lg:mb-6">
              Entre em Contato
            </h2>
            <p className="text-lg lg:text-xl text-gray-700 leading-relaxed">
              Venha conhecer nosso espaço ou tire suas dúvidas
            </p>
            <div className="w-16 lg:w-24 h-1 bg-gradient-to-r from-blue-500 to-emerald-500 mx-auto mt-6"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Endereço */}
            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <MapPin className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Endereço</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Rua Doutor Alberto Pasqualini, 70<br />
                    Sala 507 - Centro<br />
                    Santa Maria, RS - Brasil<br />
                    CEP: 97015-010
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Contato */}
            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-teal-500 to-blue-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Phone className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Contato</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Márcia Studer Ghisio<br />
                    (55) 98152-4953
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Horário de Funcionamento */}
            <Card className="text-center p-8 bg-white/90 backdrop-blur-lg border-0 shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group">
              <CardContent className="space-y-6">
                <div className="w-20 h-20 bg-gradient-to-br from-blue-500 to-emerald-500 rounded-2xl flex items-center justify-center mx-auto group-hover:scale-110 transition-transform duration-300">
                  <Clock className="w-10 h-10 text-white" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800 mb-4">Horário de Funcionamento</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Segunda a Sábado<br />
                    08:00 às 20:00
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 px-4 bg-gradient-to-br from-gray-900 via-emerald-950 to-teal-950 text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-emerald-900/20 to-teal-900/20"></div>
        <div className="container mx-auto max-w-6xl relative z-10">
          <div className="grid md:grid-cols-4 gap-8 mb-10">
            <div className="md:col-span-2">
              <div className="flex items-center gap-2.5 mb-4">
                <div className="w-9 h-9 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center shadow-md">
                  <Heart className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold bg-gradient-to-r from-emerald-400 to-teal-400 bg-clip-text text-transparent">
                  Vita Sana
                </h3>
              </div>
              <p className="text-gray-400 leading-relaxed max-w-md">
                Espaço de coworking especializado para psicólogos, terapeutas e profissionais da saúde em Santa Maria - RS.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-4">Links</h4>
              <ul className="space-y-2">
                <li><button type="button" onClick={() => { const el = document.getElementById("about"); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }); }} className="text-gray-400 hover:text-white transition-colors text-sm">Sobre Nós</button></li>
                <li><button type="button" onClick={() => { const el = document.getElementById("features"); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }); }} className="text-gray-400 hover:text-white transition-colors text-sm">Diferenciais</button></li>
                <li><button type="button" onClick={() => { const el = document.getElementById("spaces"); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }); }} className="text-gray-400 hover:text-white transition-colors text-sm">Espaços</button></li>
                <li><button type="button" onClick={() => { const el = document.getElementById("contact"); if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 80, behavior: "smooth" }); }} className="text-gray-400 hover:text-white transition-colors text-sm">Contato</button></li>
              </ul>
            </div>

            <div>
              <h4 className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-4">Contato</h4>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Rua Dr. Alberto Pasqualini, 70</li>
                <li>Sala 507 - Centro</li>
                <li>Santa Maria - RS</li>
                <li>CEP: 97015-010</li>
                <li className="pt-2">
                  <a href="https://wa.me/5555981524953" target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300 transition-colors">
                    (55) 98152-4953
                  </a>
                </li>
              </ul>
            </div>
          </div>

          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Vita Sana — Todos os direitos reservados
            </p>
            <p className="text-gray-600 text-sm">
              Desenvolvido com ❤️ para profissionais da saúde
            </p>
          </div>
        </div>
      </footer>
      <WhatsAppButton />
      <BackToTop />
    </div>
    </>
  );
};

export default Index;
